import AbstractHttpService from '@services/abstractHttpService';

import urls from '@const/urls';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  IBox,
  IBoxCreate,
  IBoxLogoUpdate,
  IBoxUpdate,
  ICurrentWebHook,
  IUser,
  IUserInvite,
  IUserInviteCreateRequest,
  IUserInviteResend,
  IUserInviteUpdate,
  IUserUpdate,
  IWebHook,
} from '@store/modules/boxes/types';

class BoxesService extends AbstractHttpService {
  private readonly url = urls.boxes;

  //----------------------------------------------------------------------------------------
  // Хуки
  //----------------------------------------------------------------------------------------
  async getWebHook(boxOguid: string): Promise<AxiosResponse<ICurrentWebHook>> {
    const url = this.url.webHookIntegration.replace('{{ boxOguid }}', boxOguid);

    return await this._http.get(url, {});
  }

  async updateWebHook(
    boxOguid: string,
    webHook: IWebHook
  ): Promise<AxiosResponse> {
    const url = this.url.webHookIntegration.replace('{{ boxOguid }}', boxOguid);

    return await this._http.put(url, webHook, {});
  }

  //----------------------------------------------------------------------------------------
  // Бизнес-боксы
  //----------------------------------------------------------------------------------------
  /** Получение списка ящиков пользователя */
  async getBoxes(): Promise<AxiosResponse<IBox[]>> {
    const url = this.url.boxes;

    return await this._http.get(url, {});
  }

  /** Создание бизнес-ящика */
  async createBox(data: IBoxCreate): Promise<AxiosResponse<string>> {
    const url = this.url.boxes;

    return await this._http.post(url, data, {});
  }

  /** Обновление бизнес-ящика */
  async updateBox(updateObj: IBoxUpdate): Promise<AxiosResponse<void>> {
    const url = this.url.changeBox.replace(
      '{{ boxOguid }}',
      updateObj.boxOguid
    );

    return await this._http.put(url, updateObj.data, {});
  }

  /** Удаление бизнес-ящика */
  async deleteBox(boxOguid: string): Promise<AxiosResponse<void>> {
    const url = this.url.changeBox.replace('{{ boxOguid }}', boxOguid);

    return await this._http.delete(url, {});
  }

  //----------------------------------------------------------------------------------------
  // Лого
  //----------------------------------------------------------------------------------------
  /** Получение лого */
  async getLogo(boxOguid: string): Promise<AxiosResponse<Blob>> {
    const url = this.url.getLogo.replace('{{ boxOguid }}', boxOguid);

    const headers = {
      Accept: 'application/octet-stream, application/json',
    };
    const config: AxiosRequestConfig = {
      params: {
        isNeedResetError: true,
      },
      headers,
      responseType: 'blob',
    };

    return await this._http.get(url, config);
  }

  /** Обновление лого */
  async setLogo(updateObj: IBoxLogoUpdate): Promise<AxiosResponse<void>> {
    const headers = {
      'Content-Type': 'multipart/form-data',
    };
    const config: AxiosRequestConfig = { headers };

    const url = this.url.changeLogo.replace(
      '{{ boxOguid }}',
      updateObj.boxOguid
    );

    return await this._http.post(url, updateObj.data, config);
  }

  /** Удаление лого */
  async deleteLogo(boxOguid: string): Promise<AxiosResponse<void>> {
    const url = this.url.changeLogo.replace('{{ boxOguid }}', boxOguid);

    return await this._http.delete(url, {});
  }

  //----------------------------------------------------------------------------------------
  // Пользователи
  //----------------------------------------------------------------------------------------
  /** Получение списка пользователей текущего бизнес-бокса */
  async getUsers(boxOguid: string): Promise<AxiosResponse<IUser[]>> {
    const url = this.url.users.list.replace('{{ boxOguid }}', boxOguid);

    return await this._http.get(url, {});
  }

  /** Обновление пользователя текущего бизнес-бокса */
  async putUser(
    boxOguid: string,
    userBoxOguid: string,
    values: IUserUpdate,
  ): Promise<AxiosResponse<void>> {
    const url = this.url.users.change
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ userBoxOguid }}', userBoxOguid);

    const config: AxiosRequestConfig = {
      params: {
        isNeedResetError: true,
      },
    };

    return await this._http.put(url, values, config);
  }

  /** Удаление пользователя из текущего бизнес-бокса */
  async deleteUser(
    boxOguid: string,
    userBoxOguid: string
  ): Promise<AxiosResponse<void>> {
    const url = this.url.users.change
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ userBoxOguid }}', userBoxOguid);

    return await this._http.delete(url, {});
  }

  //----------------------------------------------------------------------------------------
  // Приглашения
  //----------------------------------------------------------------------------------------
  /** Список приглашений пользователей */
  async getInvites(boxOguid: string): Promise<AxiosResponse<IUserInvite[]>> {
    const url = this.url.invites.list.replace('{{ boxOguid }}', boxOguid);

    return await this._http.get(url, {});
  }

  /** Создание приглашения пользователю */
  async sendInvite(
    boxOguid: string,
    invite: IUserInviteCreateRequest
  ): Promise<AxiosResponse<void>> {
    const url = this.url.invites.create.replace('{{ boxOguid }}', boxOguid);

    return await this._http.post(url, invite, {});
  }

  /** Повторение отправки приглашения пользователю */
  async repeatInvites(
    boxOguid: string,
    invites: IUserInviteResend
  ): Promise<AxiosResponse<void>> {
    const url = this.url.invites.repeat.replace('{{ boxOguid }}', boxOguid);

    return await this._http.post(url, invites, {});
  }

  /** Повторение отправки приглашения пользователю */
  async updateInvite(
    boxOguid: string,
    inviteOguid: string,
    invite: IUserInviteUpdate
  ): Promise<AxiosResponse<void>> {
    const url = this.url.invites.change
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ inviteOguid }}', inviteOguid);

    return await this._http.put(url, invite, {});
  }

  /** Удаления приглашения пользователя */
  async deleteInvite(
    boxOguid: string,
    inviteOguid: string
  ): Promise<AxiosResponse<void>> {
    const url = this.url.invites.change
      .replace('{{ boxOguid }}', boxOguid)
      .replace('{{ inviteOguid }}', inviteOguid);

    return await this._http.delete(url, {});
  }
}

export default new BoxesService();
